import api from "../../config/api";
import fetchWithCache from "../../utils/ApiCaching";

const state = {
    locations: {},
    locationsAll: {},
    locationDetail: "",
    autoEngineStatus: "",
    selectedLocation: null,
    locationSetupStatus: null,
}

const mutations = {
    setLocations(state, payload) {
        state.locations = payload;
    },
    setLocationsAll(state, payload) {
        state.locationsAll = payload;
    },
    setLocationDetails(state, payload) {
        state.locationDetail = payload;
    },
    setAutoEngineStatus(state, payload) {
        state.autoEngineStatus = payload;
    },
    addLocationToList(state, payload) {
        if (Array.isArray(state.locations)) {
            state.locations.push(payload);
        } else {
            state.locations = [payload];
        }
    },
    selectedLocation(state, payload) {
        state.selectedLocation = payload;
    },
    setLocationSetupStatus(state, payload) {
        state.locationSetupStatus = payload;
    },
};

const actions = {
    async fetchPersonLocations({ commit, rootGetters }, payload) {
        // Support both old format (direct personid) and new format (payload object)
        const personid = typeof payload === 'object' ? payload.personid : payload;
        const allrecords = typeof payload === 'object' ? payload.allrecords : undefined;
                
        try {
            const requestData = {
                cmd: "getpersonlocationsjson",
                personid: personid
            };

            // Include current locationid so the backend can ensure it appears in the list
            // (important for superadmin impersonation where the location may not be in their own list)
            const sessionData = rootGetters.stSessionData;
            const currentLocationId = sessionData?.locationid || sessionData?.locationId;
            if (currentLocationId) {
                requestData.locationid = currentLocationId;
            }
            
            const config = {};
            if (allrecords === true) {
                config.params = { allrecords: true };
            }
            
            const response = await api.post(`/personajax`, requestData, config);
            if (allrecords === true) {
                commit('setLocationsAll', response.data.output);
            } else {
                commit('setLocations', response.data.output);
            }
        }
        catch (e) {
            console.log('Error in fetching data', e);
        }
    },

    async setPersonLocations({ commit }, locationID) {
        try {
            const response = await api.post(`/managelocationajax`, {
                cmd: "setlocation",
                locationid: locationID
            });
            commit('selectedLocation', response.data.locationid);
        }
        catch (e) {
            console.log('Error in setting person location', e);
        }
    },

    async fetchlocationDetails({ commit }, locationID) {
        try {
            const response = await api.post(`/managelocationajax`, {
                cmd: "getlocationdetails",
                locationid: locationID
            });
            commit('setLocationDetails', response.data.output);
            return response.data.output
        }
        catch (e) {
            console.log('Error in fetching location details', e);
        }
    },

    // 🔹 REST endpoint
    async fetchLocationById({ commit }, locationID) {
        try {
            const response = await api.get(`/locations/${locationID}`);
            commit('setLocationDetails', response.data);
            return response.data;
        }
        catch (e) {
            console.log('Error fetching location by id', e);
            throw e;
        }
    },

    async createLocation({ commit }, payload) {
        try {
            const response = await api.post(`/locations`, payload);
            commit('addLocationToList', response.data);
            return response.data;
        }
        catch (e) {
            console.log('Error adding location', e);
            throw e;
        }
    },

    async updateLocation({ commit }, { id, payload }) {
        try {
            const response = await api.put(`/locations/${id}`, payload);
            commit('addLocationToList', response.data);
            return response.data;
        }
        catch (e) {
            console.log('Error updating location', e);
            throw e;
        }
    },

    async deleteLocation(context, locationId) {
        try {
            const response = await api.delete(`/locations/${locationId}`);
            return response.data;
        }
        catch (e) {
            console.log('Error deleting location', e);
            throw e;
        }
    },

    // 🔹 NEW: Legacy `editlocation` API
    // async getLocationManagementInfo({ commit }, locationId) {
    //     try {
    //         const response = await api.get(`/managelocation?locationid=${locationId}&cmd=editlocation`);
    //         commit('setLocationDetails', response.data);
    //         return response.data.output;
    //     }
    //     catch (e) {
    //         console.log('Error editing location', e);
    //         throw e;
    //     }
    // },

    async getLocationManagementInfo({ commit }, locationId) {
        try {
            const response = await api.get(`/managelocation?locationid=${locationId}&cmd=editlocation`);
            commit('setLocationDetails', response.data);
            return response.data.output;
        }
        catch (e) {
            console.log('Error editing location', e);
            throw e;
        }
    },

    async fetchAutoEngineStatus({ commit }, locationID) {
        const cmd = "getautoenginedt";
        const cacheKey = `apiCache_${cmd}_${locationID}`;

        // Clean up stale duplicate key from old code (no underscore separator)
        localStorage.removeItem(`apiCache_${cmd}${locationID}`);

        return await fetchWithCache({ commit }, {
            apicmd: cmd,
            cacheKey: cacheKey,
            cacheDuration: 30000, // 30 seconds
            postData: { cmd: cmd, locationid: locationID },
            mutation: 'setAutoEngineStatus',
            url: `/manageuserajax`,
        });
    },

    // What's Next api
    async fetchLocationSetupStatus({ commit }, locationId) {
        try {
            const response = await api.get(`/locations/${locationId}/setup-status`);
            commit('setLocationSetupStatus', response.data.data);
            return response.data.data;
        }
        catch (e) {
            console.log('Error fetching location setup status', e);
            throw e;
        }
    }
}

const getters = {
    stLocations: state => state.locations,
    stLocationsAll: state => state.locationsAll,
    stLocationDetail: state => state.locationDetail,
    stAutoEngineStatus: state => state.autoEngineStatus,
    stLocationSetupStatus: state => state.locationSetupStatus,
}

export default {
    state,
    mutations,
    actions,
    getters
}
